/**
 * The product rule for which zone a user sees — mirrored by the backend's
 * TimeZones.resolve so /api/profile's effectiveTimezone and this agree.
 *
 *   explicit personal choice -> organization default -> browser-detected -> UTC
 *
 * Owner decision (2026-10-15): "it's org's first, then users can change it".
 * `userTz` is ONLY a zone the user deliberately chose — the backend reports
 * `profile.timezone` for an explicit choice and null otherwise (a stored but
 * never-chosen value, e.g. one the old profile page pre-filled from the
 * browser, is not reported), so the organization zone wins for everyone else.
 * The browser zone is only the fallback when the org has none, and UTC when
 * the browser cannot name a usable zone.
 *
 * A zone the browser cannot render (legacy data, a zone newer than this
 * browser's tz database) is skipped rather than trusted: Intl would throw on
 * it and every timestamp on the page would fall back inconsistently.
 */

export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
};

const validity = new Map();

/** True when `tz` is a non-empty zone this browser's Intl accepts. */
export const isValidTimezone = (tz) => {
  if (!tz || typeof tz !== 'string' || !tz.trim()) return false;
  const key = tz.trim();
  if (validity.has(key)) return validity.get(key);
  let ok;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: key });
    ok = true;
  } catch (_) {
    ok = false;
  }
  validity.set(key, ok);
  return ok;
};

/**
 * @returns {{tz: string, source: 'user'|'org'|'detected'|'utc'}}
 */
export const resolveTimezone = ({ userTz, orgTz, detectedTz } = {}) => {
  if (isValidTimezone(userTz)) return { tz: userTz.trim(), source: 'user' };
  if (isValidTimezone(orgTz)) return { tz: orgTz.trim(), source: 'org' };
  const detected = isValidTimezone(detectedTz) ? detectedTz.trim() : browserTimezone();
  if (isValidTimezone(detected)) return { tz: detected.trim(), source: 'detected' };
  return { tz: 'UTC', source: 'utc' };
};

/** Human label for a source, for "Times shown in X (…)" hints. */
export const sourceLabel = (source) => {
  switch (source) {
    case 'user': return 'your profile';
    case 'org': return 'organization default';
    case 'utc': return 'UTC fallback';
    default: return 'detected from this browser';
  }
};
